import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { C, GRADIENT_BRAND_DARK } from '@parsina/framework/tokens';
import { BottomSheet, DateRangeFilter, useToast } from '@parsina/framework';
import { CreditsHistorySkeleton } from './CreditsSkeleton';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import { useWalletBalance } from '@parsina/framework/clinical/src/hooks/useWalletBalance';
import { useWalletTransactions } from '../../hooks/useWalletTransactions';
import { useTopUp } from '../../hooks/useTopUp';
import { readPaymentOutcome, stripPaymentParam } from '../../lib/paymentReturn';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { creditLevel, creditColor, CREDIT_LEVEL_META } from '@parsina/framework/clinical/src/constants/credit';
import { fmtCoins, coinsToMoneyLabel } from '@parsina/framework/clinical/src/constants/creditPricing';
import { useCreditPrices } from '@parsina/framework/clinical/src/lib/useCreditPrices';
import { useCreditPurchase } from '../../hooks/useCreditPurchase';
import { giftCodesEnabled } from '../../lib/runtimeEnv';
import { type } from '@parsina/framework/clinical/src/constants/typography';
import SegmentedControl from '../../components/common/SegmentedControl';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import MobileHeader from '@parsina/framework/clinical/src/components/mobile/MobileHeader';
import { CoinGlyph } from '@parsina/framework/clinical/src/components/common/CoinChip';
import TopUpSheet from './TopUpSheet';
import GiftCodeCard from './GiftCodeCard';
import { getServiceMeta, transactionLabels, txnTime, groupByDay, groupByType, computeStats, DATE_PRESETS, rangeToISO } from './Credits.shared';

const PAGE_SIZE = 30;
const CARD = { background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(20,30,45,0.05)' };
const FILTERS = (t) => ([{ id: 'all', label: t('common.state.all') }, { id: 'debit', label: t('credits.spend') }, { id: 'credit', label: t('credits.topUp') }]);
const GROUP_OPTIONS = (t) => ([{ id: 'day', label: t('common.units.day') }, { id: 'type', label: t('common.labels.type') }]);

function MobileTxnRow({ txn }) {
  const { t, has, locale, formatNumber, formatDate, formatTime } = useTranslation();
  const meta = getServiceMeta(txn.serviceType, t);
  const IconC = meta.icon;
  const isDebit = txn.type === 'debit';
  const { title, sub } = transactionLabels(txn, { t, has, locale, formatNumber });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderTop: `1px solid ${C.border}` }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <IconC size={15} color={meta.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...type.label, fontWeight: 700, color: C.textPrimary }}>{title || meta.label}</div>
        <div style={{ ...type.micro, fontWeight: 600, color: C.textMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {txnTime(txn.createdAt, formatTime)}{sub ? ` · ${sub}` : ''}
        </div>
      </div>
      <span style={{ ...type.body, fontWeight: 800, color: isDebit ? C.textSecondary : C.green }}>{isDebit ? '−' : '+'}{fmtCoins(txn.amount, formatNumber)}</span>
    </div>
  );
}

function MobileTypeGroupHeader({ group }) {
  const { t, formatNumber } = useTranslation();
  const IconC = group.meta.icon;
  const spent = group.total >= 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', background: C.bg }}>
      <span style={{ width: 20, height: 20, borderRadius: 6, background: group.meta.bg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <IconC size={12} color={group.meta.color} />
      </span>
      <span style={{ ...type.caption, fontWeight: 800, flex: 1, color: C.textPrimary }}>{group.meta.label}</span>
      <span style={{ ...type.micro, fontWeight: 600, color: C.textMuted }}>{fmtCoins(group.items.length, formatNumber)} {t('dashboard.queue.finishedCount')}</span>
      <span style={{ ...type.label, fontWeight: 800, color: spent ? C.textSecondary : C.green }}>{spent ? '−' : '+'}{fmtCoins(Math.abs(group.total), formatNumber)}</span>
    </div>
  );
}

export default function CreditsMobile() {
  const { t, formatNumber, formatDate, locale } = useTranslation();
  // The policy's figures once the list has arrived; the shipped constants until then.
  const { priceList } = useCreditPrices();
  const { doctor } = useDoctor();
  const navigate = useAppNavigate();
  const balance = useWalletBalance(doctor?.id);
  const { startTopUp, busy: payBusy, error: payError } = useTopUp(doctor?.id);
  // Buying credit only where it is sold and this panel has a rail for its price
  // (Credits.web.jsx says why): otherwise no buy button, money figure, price
  // list or top-up sheet.
  const { canBuy, creditPrice } = useCreditPurchase();

  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [groupMode, setGroupMode] = useState('day');       // 'day' | 'type'
  const [datePreset, setDatePreset] = useState('all');
  const [dateCustom, setDateCustom] = useState({ from: '', to: '' });
  const [priceOpen, setPriceOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const { startDate, endDate } = useMemo(() => rangeToISO(datePreset, dateCustom), [datePreset, dateCustom]);
  const { transactions, totalCount, loading, error, refetch } = useWalletTransactions(doctor?.id, {
    limit: PAGE_SIZE, offset: page * PAGE_SIZE, startDate, endDate,
    // Server-side: the ledger runs to thousands of rows, so filtering the
    // fetched page would show only the matches that fall on it.
    type: typeFilter === 'all' ? '' : typeFilter,
  });
  const stats = useMemo(() => computeStats(transactions), [transactions]);
  const filtered = useMemo(
    () => transactions,
    [transactions],
  );
  const dayGroups  = useMemo(() => groupByDay(filtered, t, formatDate),  [filtered, t, formatDate]);
  const typeGroups = useMemo(() => groupByType(filtered, t), [filtered, t]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Gateway return leg — the same one Credits.web has. Without it a doctor who
  // paid on a phone landed back on this screen with no confirmation and a stale
  // balance, because the ≤768px viewport renders THIS component, not the web one.
  const toast = useToast();
  useEffect(() => {
    const settled = readPaymentOutcome();
    if (!settled) return;
    stripPaymentParam();
    if (settled === 'success') toast.success(t('credits.paymentDone'));
    else toast.error(t('errors.payment.failedRefund'));
    refetch?.();
  }, [toast, refetch]);
  const applyDatePreset = (id) => { setDatePreset(id); setPage(0); };
  const applyDateCustom = (from, to) => { setDateCustom({ from, to }); setDatePreset('custom'); setPage(0); };
  const level = creditLevel(balance);
  const topMeta = stats.topService ? getServiceMeta(stats.topService, t) : null;

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>
      <div style={{ flexShrink: 0 }}>
        <MobileHeader title={t('dashboard.rail.smartCredit')} onBack={() => navigate('reception')} backLabel={t('accounting.backToReception')} />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 0.875rem 1.5rem' }}>

        {/* Hero — rides the header edge */}
        <div style={{ background: GRADIENT_BRAND_DARK, borderRadius: 16, padding: 16, color: '#fff', marginTop: -14, boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }}>
          <div style={{ ...type.caption, color: 'rgba(255,255,255,0.6)' }}>{t('credits.currentBalance')}</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginTop: 4 }}>
            <span style={{ ...type.display }}>{balance === null ? '—' : fmtCoins(balance, formatNumber)}</span>
            <span style={{ ...type.unit, color: 'rgba(255,255,255,0.7)', marginBottom: 3 }}>{t('common.units.coin')}</span>
            {balance !== null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.14)', borderRadius: 20, padding: '2px 9px', marginBottom: 3 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: creditColor(balance) }} />
                <span style={{ ...type.micro, fontWeight: 600 }}>{t(CREDIT_LEVEL_META[level].labelKey)}</span>
              </span>
            )}
          </div>
          {canBuy && balance !== null && <div style={{ ...type.micro, fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginTop: 5 }}>{coinsToMoneyLabel(balance, { creditPrice, formatNumber, tomanLabel: t('common.units.toman'), locale })}</div>}
          {canBuy && (
          <button onClick={() => setTopUpOpen(true)} data-testid="credits-topup"
            style={{ ...type.body, marginTop: 12, width: '100%', boxSizing: 'border-box', height: 42, borderRadius: 10, border: 'none', background: '#fff', color: C.navy, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <CoinGlyph size={14} /> {t('credits.buyCoins')}
          </button>
          )}
        </div>

        {/* KPI tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 10 }}>
          {[
            { v: fmtCoins(stats.usedThisMonth, formatNumber), l: t('credits.monthSpend') },
            { v: stats.avgPerVisit == null ? '—' : fmtCoins(stats.avgPerVisit, formatNumber), l: t('credits.perVisit') },
            { v: topMeta ? t('credits.percentSuffix', { value: fmtCoins(stats.topPct, formatNumber) }) : '—', l: topMeta ? topMeta.label : t('credits.topService') },
          ].map((item, i) => (
            <div key={i} style={{ ...CARD, padding: 10, textAlign: 'center' }}>
              <div style={{ ...type.amount, color: C.textPrimary }}>{item.v}</div>
              <div style={{ ...type.micro, fontWeight: 600, color: C.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.l}</div>
            </div>
          ))}
        </div>

        {/* Gift code — above the price list, because a doctor arriving from
            the campaign has a code in hand and nothing else to do here.
            `compact` drops the two explanatory lines: on a phone they push the
            input itself below the fold. */}
        {giftCodesEnabled() && <GiftCodeCard doctorId={doctor?.id} compact onRedeemed={() => refetch && refetch()} />}

        {/* Price-list accordion */}
        {canBuy && (
        <div style={{ ...CARD, marginTop: 10, padding: '10px 12px' }}>
          <button onClick={() => setPriceOpen(o => !o)} style={{ width: '100%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', cursor: 'pointer', padding: 0 }}>
            <b style={{ ...type.label, fontWeight: 800, flex: 1, color: C.textPrimary, textAlign: 'start' }}>{t('credits.priceListTitle')}</b>
            <span style={{ color: C.textMuted, fontSize: '0.75rem', transform: priceOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>⌄</span>
          </button>
          {priceOpen && (
            <div style={{ marginTop: 8 }}>
              {priceList.map((r, i) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: i ? `1px solid ${C.border}` : 'none' }}>
                  <span style={{ ...type.caption, flex: 1, color: C.textSecondary }}>{t(r.labelKey)}</span>
                  <span style={{ ...type.caption, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 4, color: C.teal }}>
                    <CoinGlyph size={10} />{fmtCoins(r.cost, formatNumber)}{r.perKey ? ` / ${t(r.perKey)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Transactions */}
        <div style={{ ...CARD, marginTop: 10, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
            <b style={{ ...type.label, fontWeight: 800, flex: 1, color: C.textPrimary }}>{t('credits.transactions')}</b>
            <SegmentedControl items={FILTERS(t)} value={typeFilter}
              onChange={(v) => { setTypeFilter(v); setPage(0); }}
              size="sm" ariaLabel={t('credits.filterType')} />
          </div>

          {/* Toolbar: grouping + date range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>
            <SegmentedControl items={GROUP_OPTIONS(t)} value={groupMode} onChange={setGroupMode} size="sm" ariaLabel={t('credits.groupBy')} />
            <div style={{ marginInlineStart: 'auto' }} data-testid="credits-daterange">
              <DateRangeFilter presets={DATE_PRESETS(t)} preset={datePreset} custom={dateCustom} onPreset={applyDatePreset} onCustom={applyDateCustom} />
            </div>
          </div>

          {error && !loading && transactions.length === 0 && (
            <ErrorState error={error} title={t('credits.transactionsUnavailable')} onRetry={refetch} />
          )}
          {loading && transactions.length === 0 && (
            <CreditsHistorySkeleton rows={6} mobile />
          )}
          {!loading && !error && filtered.length === 0 && (
            <div style={{ padding: '32px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.875rem', marginBottom: 8 }}>🪙</div>
              <div style={{ ...type.body, color: C.textPrimary }}>{(typeFilter === 'all' && datePreset === 'all') ? t('credits.noTransactions') : t('errors.empty.transactions')}</div>
            </div>
          )}

          {!loading && groupMode === 'day' && dayGroups.map(group => (
            <div key={group.key}>
              <div style={{ ...type.micro, fontWeight: 700, padding: '5px 12px', color: C.textMuted, background: C.bg }}>{group.label}</div>
              {group.items.map(txn => <MobileTxnRow key={txn.id} txn={txn} />)}
            </div>
          ))}
          {!loading && groupMode === 'type' && typeGroups.map(group => (
            <div key={group.key}>
              <MobileTypeGroupHeader group={group} />
              {group.items.map(txn => <MobileTxnRow key={txn.id} txn={txn} />)}
            </div>
          ))}

          {/* Pager, not «بارگذاری بیشتر»: the hook replaces its data rather than
              appending, so that button discarded the rows already on screen and
              gave no way back to them. */}
          {totalPages > 1 && !loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, padding: '10px 12px', borderTop: `1px solid ${C.border}` }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                data-testid="txn-prev"
                style={{ ...type.label, height: 34, padding: '0 16px', background: C.card,
                  border: `1px solid ${C.border}`, borderRadius: 9,
                  color: page === 0 ? C.textMuted : C.textPrimary,
                  opacity: page === 0 ? 0.55 : 1,
                  cursor: page === 0 ? 'not-allowed' : 'pointer' }}>
                {t('common.actions.previous')}
              </button>
              <span style={{ ...type.label, color: C.textSecondary, whiteSpace: 'nowrap' }}
                data-testid="txn-page-label">
                {t('common.pagination.pageLabel')} {fmtCoins(page + 1, formatNumber)} {t('common.labels.from')} {fmtCoins(totalPages, formatNumber)}
              </span>
              <button onClick={() => setPage(p => (p + 1 < totalPages ? p + 1 : p))}
                disabled={page + 1 >= totalPages} data-testid="txn-next"
                style={{ ...type.label, height: 34, padding: '0 16px', background: C.card,
                  border: `1px solid ${C.border}`, borderRadius: 9,
                  color: page + 1 >= totalPages ? C.textMuted : C.textPrimary,
                  opacity: page + 1 >= totalPages ? 0.55 : 1,
                  cursor: page + 1 >= totalPages ? 'not-allowed' : 'pointer' }}>
                {t('common.actions.next')}
              </button>
            </div>
          )}
        </div>
      </div>

      {canBuy && (
      <BottomSheet open={topUpOpen} onClose={() => setTopUpOpen(false)} title={t('credits.buyCoins')} closeLabel={t('common.actions.close')}>
        <TopUpSheet avgPerVisit={stats.avgPerVisit} balance={balance} onPay={startTopUp} busy={payBusy} error={payError} />
      </BottomSheet>
      )}
    </div>
  );
}
